import React, { useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { lyricLineCount, lyricSections, searchLyricLines, suggestedLines } from 'fym-shared/src/music24/lyricSections';
import type { KitFontId, ReleaseThemeId } from 'fym-shared/src/music24/releaseThemes';
import type { StoryStyle } from 'fym-shared/src/music24/story/storyTypes';
import { TypedMemo } from '../../../../../common/types';
import { LyricFitFoot } from './LyricFitFoot';
import { LyricLineGroups } from './LyricLineGroups';
import { lyricPanelMessages } from './lyricPanelMessages';
import { LyricPanelEmpty } from './LyricPanelEmpty';
import { LyricPanelHeader } from './LyricPanelHeader';
import { LyricSearchBox } from './LyricSearchBox';
import { LyricSearchResults } from './LyricSearchResults';
import { LyricSheetTrigger, LyricSheetUse } from './LyricSheetParts';
import { moveRowFocus } from './moveRowFocus';
import { useLyricFit } from './useLyricFit';
import { useLyricPick } from './useLyricPick';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the lyric panel; each part uses its own slice of it */
import styles from './LyricLinePanel.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** The search shows from this many lines (design b3). */
export const SEARCH_MIN_LINES = 12;

export interface LyricLinePanelProps {
  assetTypeId: 'story' | 'feedPost' | 'lyricCard';
  lyrics: string | null;
  /** The stored value: one line, or two lines joined with "\n". */
  value?: string;
  onChange: (lyricLine: string) => void;
  releaseThemeId: ReleaseThemeId;
  fontOverride: KitFontId | null;
  /** The Story style of the step: the fit meter of Type only uses its larger lyric. */
  storyStyle?: StoryStyle;
  /** Opens the song page lyrics (for the empty state "Add lyrics"). */
  onOpenSongLyrics?: () => void;
}

/**
 * The lyric line panel (design `studio-details` frame b): sections with headers, "Suggested",
 * search, "1 line / 2 lines" and the fit meter. Each pick calls `onChange` at once, so the preview
 * follows. On a phone (below 576 px) the panel is a bottom sheet.
 */
const LyricLinePanelComponent: React.FC<LyricLinePanelProps> = ({
  assetTypeId,
  lyrics,
  value,
  onChange,
  releaseThemeId,
  fontOverride,
  storyStyle,
  onOpenSongLyrics,
}) => {
  const intl = useIntl();
  const [query, setQuery] = useState('');
  const [sheetOpen, setSheetOpen] = useState(false);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const sections = useMemo(() => lyricSections(lyrics), [lyrics]);
  const suggestions = useMemo(() => suggestedLines(sections), [sections]);
  const lineCount = useMemo(() => lyricLineCount(sections), [sections]);
  const hits = useMemo(() => searchLyricLines(sections, query), [query, sections]);
  const pick = useLyricPick(sections, value, onChange);
  const text = value ?? '';
  const fit = useLyricFit({
    value: text,
    assetTypeId,
    releaseThemeId,
    fontOverride,
    twoLines: pick.mode === 'two',
    storyStyle,
  });
  const hasLines = lineCount > 0;
  const searching = query.trim() !== '';
  const withSearch = lineCount >= SEARCH_MIN_LINES;

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    const target = event.target as HTMLElement;
    if (event.key === '/' && target.tagName !== 'INPUT' && searchRef.current !== null) {
      event.preventDefault();
      searchRef.current.focus();
      return;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (moveRowFocus(event.currentTarget, target, event.key === 'ArrowDown' ? 1 : -1)) {
        event.preventDefault();
      }
    }
  };

  const body = (() => {
    if (!hasLines) {
      return (
        <LyricPanelEmpty
          title={intl.formatMessage(lyricPanelMessages.noLyricsTitle)}
          text={intl.formatMessage(lyricPanelMessages.noLyricsText)}
          action={
            onOpenSongLyrics === undefined
              ? undefined
              : { label: intl.formatMessage(lyricPanelMessages.addLyrics), onClick: onOpenSongLyrics }
          }
        />
      );
    }
    if (searching && hits.length === 0) {
      return (
        <LyricPanelEmpty
          title={intl.formatMessage(lyricPanelMessages.noHitTitle, { query: query.trim() })}
          text={intl.formatMessage(lyricPanelMessages.noHitText)}
          action={{ label: intl.formatMessage(lyricPanelMessages.clearSearch), onClick: () => setQuery('') }}
        />
      );
    }
    return (
      <div
        className={styles.body}
        data-lyric-list
        role="radiogroup"
        aria-label={intl.formatMessage(lyricPanelMessages.title)}
      >
        {searching ? (
          <LyricSearchResults sections={sections} hits={hits} query={query} pick={pick} />
        ) : (
          <LyricLineGroups sections={sections} suggestions={suggestions} pick={pick} />
        )}
      </div>
    );
  })();

  return (
    <div className={styles.wrap}>
      <LyricSheetTrigger value={text} onOpen={() => setSheetOpen(true)} />
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions -- the arrow keys move between the row buttons inside */}
      <div
        className={sheetOpen ? `${styles.panel} ${styles.panelOpen}` : styles.panel}
        data-testid="lyric-line-panel"
        onKeyDown={onKeyDown}
      >
        <span className={styles.handle} aria-hidden="true" />
        <LyricPanelHeader lineCount={lineCount} mode={hasLines ? pick.mode : null} onMode={pick.setMode} />
        {hasLines && withSearch && (
          <LyricSearchBox query={query} hitCount={hits.length} onQuery={setQuery} inputRef={searchRef} />
        )}
        {body}
        {hasLines && text !== '' && <LyricFitFoot fit={fit} twoLines={pick.mode === 'two'} />}
        {hasLines && (
          <LyricSheetUse value={text} chars={fit.chars} state={fit.state} onClose={() => setSheetOpen(false)} />
        )}
      </div>
    </div>
  );
};

export const LyricLinePanel = TypedMemo(LyricLinePanelComponent);
